import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, Calendar, Home, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dedicated post-payment confirmation page.
 * URL: /payments/success/:paymentId
 * Polls the payment doc once. If it's already confirmed we show the full
 * green celebration. If it's still pending (webhook lag), we show a
 * "finalizing your consultation…" state and poll for up to 30s.
 */
export const PaymentSuccessPage = () => {
  const { paymentId } = useParams();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  // Countdown for the auto-redirect back to the page the user came from.
  // `null` = no return path found → skip the countdown, show default CTAs.
  const [returnTo, setReturnTo] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [autoRedirect, setAutoRedirect] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/payments/${paymentId}`, { withCredentials: true });
        if (cancelled) return;
        setPayment(r.data);
        // Resolve return_to: prefer the value stored server-side on the
        // payment, fall back to sessionStorage captured at initiate time.
        // Only accept safe in-app paths.
        const rawCandidate = r.data?.return_to || (() => {
          try { return sessionStorage.getItem(`payment_return_to_${paymentId}`); }
          catch { return null; }
        })();
        if (
          rawCandidate &&
          typeof rawCandidate === 'string' &&
          rawCandidate.startsWith('/') &&
          !rawCandidate.startsWith('//') &&
          !rawCandidate.includes('://')
        ) {
          setReturnTo(rawCandidate);
        }
        // If the webhook hasn't landed yet, nudge the fallback endpoint and
        // schedule another read.
        if (r.data.status === 'pending' && attempts < 10) {
          attempts += 1;
          if (r.data.provider === 'whish') {
            axios.post(`${API}/payments/${paymentId}/whish/status`, {}, { withCredentials: true }).catch(() => {});
          } else if (r.data.provider === 'omt') {
            axios.post(`${API}/payments/${paymentId}/omt/status`, {}, { withCredentials: true }).catch(() => {});
          }
          setTimeout(load, 3000);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load payment');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [paymentId]);

  // Auto-redirect countdown — only starts once payment is confirmed AND a
  // valid return_to exists AND user hasn't cancelled it.
  const isConfirmedForRedirect = payment?.status === 'confirmed';
  useEffect(() => {
    if (!isConfirmedForRedirect || !returnTo || !autoRedirect) return undefined;
    if (countdown <= 0) {
      // Clean up the sessionStorage entry now that we're navigating home.
      try { sessionStorage.removeItem(`payment_return_to_${paymentId}`); } catch { /* noop */ }
      navigate(returnTo, { replace: true });
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [isConfirmedForRedirect, returnTo, autoRedirect, countdown, navigate, paymentId]);

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  if (error) {
    return (
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center p-4 ${dir}`} dir={dir}>
        <Card className="max-w-lg w-full p-8 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/subscriptions')} data-testid="success-error-retry">
            {t('العودة إلى الباقات', 'Back to packages', 'Retour aux forfaits')}
          </Button>
        </Card>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className={`min-h-screen bg-gray-50 flex items-center justify-center ${dir}`} dir={dir}>
        <Loader2 className="animate-spin text-[#1e88e5]" size={40} />
      </div>
    );
  }

  const isConfirmed = payment.status === 'confirmed';
  const amountFmt = `${payment.amount} ${(payment.currency || 'USD').toUpperCase()}`;
  const packageName = language === 'ar' ? (payment.package_name_ar || payment.package_name_en) : (payment.package_name_en || payment.package_name_ar);

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4 ${dir}`} dir={dir} data-testid="payment-success-page">
      <Card className="max-w-xl w-full p-8 md:p-10 shadow-xl border-2 border-green-100">
        <div className="text-center">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-5 ${isConfirmed ? 'bg-green-500' : 'bg-amber-500'} text-white`}>
            {isConfirmed
              ? <CheckCircle2 size={44} data-testid="success-icon-confirmed" />
              : <Loader2 className="animate-spin" size={44} data-testid="success-icon-pending" />
            }
          </div>

          <h1 className="text-3xl font-bold text-[#2c3e50] mb-2" data-testid="success-title">
            {isConfirmed
              ? t('تم الدفع بنجاح!', 'Payment Successful!', 'Paiement Réussi !')
              : t('جاري تأكيد الدفع...', 'Finalizing your payment…', 'Finalisation du paiement…')
            }
          </h1>

          <p className="text-gray-600 mb-8" data-testid="success-subtitle">
            {isConfirmed
              ? t('تم تأكيد استشارتك ويمكنك البدء الآن.',
                  'Your consultation is confirmed and ready to use.',
                  'Votre consultation est confirmée.')
              : t('نقوم بالتحقق مع ويش. لن يستغرق ذلك سوى بضع ثوانٍ...',
                  'We are checking with Whish. This usually takes a few seconds…',
                  'Vérification avec Whish, quelques secondes…')
            }
          </p>

          <div className="bg-gray-50 rounded-2xl p-5 mb-8 text-start" data-testid="success-details">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">{t('الاستشارة', 'Consultation', 'Consultation')}</span>
              <span className="font-semibold text-[#2c3e50]" data-testid="success-package">{packageName}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">{t('المبلغ المدفوع', 'Amount paid', 'Montant payé')}</span>
              <span className="font-bold text-[#1e88e5] text-lg" data-testid="success-amount">{amountFmt}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">{t('طريقة الدفع', 'Payment method', 'Moyen de paiement')}</span>
              <span className="font-semibold uppercase text-gray-700" data-testid="success-provider">{payment.provider}</span>
            </div>
            {payment.whish_transaction_id && (
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500">{t('رقم المعاملة', 'Transaction ID', 'ID Transaction')}</span>
                <span className="font-mono text-xs text-gray-700" data-testid="success-tx-id">{payment.whish_transaction_id}</span>
              </div>
            )}
            {payment.omt_receipt && (
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-sm text-gray-500">{t('رقم الإيصال', 'Receipt', 'Reçu')}</span>
                <span className="font-mono text-xs text-gray-700" data-testid="success-receipt">{payment.omt_receipt}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-500">{t('المرجع', 'Reference', 'Référence')}</span>
              <span className="font-mono text-xs text-gray-700" data-testid="success-reference">{payment.reference}</span>
            </div>
          </div>

          {isConfirmed && returnTo && autoRedirect && (
            <div
              className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-3"
              data-testid="success-auto-redirect-banner"
            >
              <div className="text-start">
                <p className="text-sm font-semibold text-[#1565c0]">
                  {payment.package_kind === 'physio' || payment.package_kind === 'psychology'
                    ? t('متابعة لتعبئة معلومات الموعد', 'Continuing to your booking form', 'Vers le formulaire de réservation')
                    : t('العودة إلى قائمة الأطباء', 'Returning to the doctors list', 'Retour à la liste des médecins')}
                </p>
                <p className="text-xs text-blue-700 mt-0.5" data-testid="success-return-path">
                  {returnTo}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-[#1565c0]" data-testid="success-countdown">
                  {t(`خلال ${countdown} ثوانٍ`, `in ${countdown}s`, `dans ${countdown}s`)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => setAutoRedirect(false)}
                  data-testid="success-stay-here"
                >
                  {t('البقاء هنا', 'Stay here', 'Rester ici')}
                </Button>
              </div>
            </div>
          )}

          {isConfirmed && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="success-actions">
              {returnTo ? (
                <Button
                  onClick={() => {
                    try { sessionStorage.removeItem(`payment_return_to_${paymentId}`); } catch { /* noop */ }
                    navigate(returnTo, { replace: true });
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 rounded-xl text-base"
                  data-testid="success-cta-return"
                >
                  <ArrowRight size={16} className="me-2" />
                  {payment.package_kind === 'physio' || payment.package_kind === 'psychology'
                    ? t('تعبئة معلومات الموعد', 'Fill Booking Info', 'Remplir le formulaire')
                    : t('العودة الآن', 'Return Now', 'Retour maintenant')}
                </Button>
              ) : payment.package_kind === 'physio' ? (
                <Button
                  onClick={() => navigate(`/physio/book/${payment.package_id}`)}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 rounded-xl text-base"
                  data-testid="success-cta-physio-form"
                >
                  <Calendar size={18} className="me-2" />
                  {t('املأ استمارة المعلومات', 'Fill Booking Info', 'Remplir le formulaire')}
                  <ArrowRight size={16} className="ms-2" />
                </Button>
              ) : payment.package_kind === 'psychology' ? (
                <Button
                  onClick={() => navigate('/psychology/doctors')}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 rounded-xl text-base"
                  data-testid="success-cta-psychology-doctors"
                >
                  <Calendar size={18} className="me-2" />
                  {t('اختر طبيبك', 'Choose Your Doctor', 'Choisir votre médecin')}
                  <ArrowRight size={16} className="ms-2" />
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/patient')}
                  className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 rounded-xl text-base"
                  data-testid="success-cta-appointments"
                >
                  <Calendar size={18} className="me-2" />
                  {t('عرض مواعيدي', 'Go to My Appointments', 'Mes rendez-vous')}
                  <ArrowRight size={16} className="ms-2" />
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="h-12 px-6 rounded-xl text-base"
                data-testid="success-cta-home"
              >
                <Home size={18} className="me-2" />
                {t('الصفحة الرئيسية', 'Home', 'Accueil')}
              </Button>
            </div>
          )}

          {!isConfirmed && (
            <Link to={`/payments/${paymentId}`} className="text-sm text-[#1e88e5] hover:underline" data-testid="success-fallback-link">
              {t('عرض حالة الطلب', 'View payment status', 'Voir le statut')}
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
